import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { AnimatePresence } from "framer-motion";
import { useLanguage } from "./i18n";

// Import refactored components
import { SBER, API, tg, maxApp, webApp, isMiniApp, getMiniAppUser, platform } from "./wallet/utils/constants";

// Import all pages from wallet/pages
import {
  WalletHome,
  HistoryPage,
  TopupPage,
  ExchangePage,
  WithdrawPage,
  MIAPaymentPage,
  PaymentsPage,
  ProfilePage,
  SupportChatPage,
  NotificationsPage,
  DocumentUploadPage,
  WebAuthScreen
} from "./wallet/pages";

// Import components
import { BottomNav } from "./wallet/components/UIComponents";

import { RefreshCw } from "lucide-react";

// Main Wallet App
export default function WalletApp() {
  const { t, language, setLanguage, currentLanguage, supportedLanguages: languages } = useLanguage();
  const [page, setPage] = useState('home');
  const [user, setUser] = useState(null);
  const [rate, setRate] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authMode, setAuthMode] = useState('register'); // login, register, authenticated
  
  // Get user from any Mini App platform (Telegram or MAX)
  const miniAppUser = getMiniAppUser();
  const isInMiniApp = isMiniApp;
  
  // Check for saved web session and validate token
  useEffect(() => {
    const validateSession = async () => {
      const savedToken = localStorage.getItem('wallet_token');
      const savedUser = localStorage.getItem('wallet_user');
      
      if (!savedToken || !savedUser) {
        // No saved session
        localStorage.removeItem('wallet_user');
        localStorage.removeItem('wallet_token');
        return;
      }
      
      try {
        // Validate token with server
        const res = await axios.post(`${API}/wallet/validate-session?token=${encodeURIComponent(savedToken)}`);
        
        if (res.data.valid && res.data.user) {
          // Session is valid, use server data (most up-to-date)
          const userData = { ...res.data.user, auth_type: 'web' };
          setUser(userData);
          setIsAuthenticated(true);
          setAuthMode('authenticated');
          // Update localStorage with fresh data from server
          localStorage.setItem('wallet_user', JSON.stringify(userData));
        } else {
          // Session is invalid, clear localStorage
          console.log('Session invalid, clearing localStorage');
          localStorage.removeItem('wallet_user');
          localStorage.removeItem('wallet_token');
          setUser(null);
          setIsAuthenticated(false);
          setAuthMode('login');
        }
      } catch (e) {
        console.error('Session validation error:', e);
        // On error, clear session to be safe
        localStorage.removeItem('wallet_user');
        localStorage.removeItem('wallet_token');
      }
    };
    
    validateSession();
  }, []);
  
  const fetchNotificationCount = useCallback(async (telegramId) => {
    try {
      const res = await axios.get(`${API}/user/notifications?telegram_id=${telegramId}&limit=1`);
      setNotificationCount(res.data.unread_count || 0);
    } catch (e) {}
  }, []);
  
  const fetchUserData = useCallback(async (userId) => {
    try {
      const token = localStorage.getItem('wallet_token');
      if (!token) return;
      const res = await axios.get(`${API}/wallet/user`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) {
        setUser(prev => {
          const updatedUser = { ...prev, ...res.data };
          localStorage.setItem('wallet_user', JSON.stringify(updatedUser));
          return updatedUser;
        });
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem('wallet_user');
        localStorage.removeItem('wallet_token');
        setUser(null);
        setIsAuthenticated(false);
        setAuthMode('login');
      }
    }
  }, []);
  
  const fetchData = useCallback(async () => {
    console.log('fetchData called, miniAppUser:', miniAppUser, 'user:', user);
    try {
      // Add timeout to prevent infinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const token = localStorage.getItem('wallet_token');
      const txRequest = miniAppUser
        ? axios.get(`${API}/user/${miniAppUser.id}/wallet`, { signal: controller.signal }).then(r => ({ data: r.data.transactions || [] }))
        : token
          ? axios.get(`${API}/wallet/transactions?limit=20`, { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
          : Promise.resolve({ data: [] });
      const [rateRes, txRes] = await Promise.all([
        axios.get(`${API}/rate`, { signal: controller.signal }),
        txRequest.catch(() => ({ data: [] }))
      ]);
      
      clearTimeout(timeoutId);
      
      setRate(rateRes.data);
      setTransactions(txRes.data);
      
      // If Mini App user (Telegram or MAX)
      if (miniAppUser) {
        const userData = {
          telegram_id: miniAppUser.id,
          user_id: miniAppUser.id,
          username: miniAppUser.username || 'user',
          first_name: miniAppUser.first_name || 'Пользователь',
          balance_usdt: 0,
          reserved_usdt: 0,
          kyc_status: 'NOT_STARTED',
          has_pin: false,
          auth_type: platform // 'telegram' or 'max'
        };
        setUser(userData);
        setIsAuthenticated(true);
        setAuthMode('authenticated');
        fetchNotificationCount(miniAppUser.id);
      } else if (user && isAuthenticated) {
        // Web user already authenticated
        fetchNotificationCount(user.telegram_id || user.user_id);
        fetchUserData(user.user_id);
      }
    } catch (e) {
      console.error('fetchData error:', e);
      // Still show UI even if data fetch fails
    } finally {
      console.log('fetchData finished, setting loading to false');
      setLoading(false);
    }
  }, [miniAppUser, user, isAuthenticated, fetchNotificationCount, fetchUserData]);
  
  useEffect(() => {
    // Initialize any Mini App (Telegram or MAX)
    if (webApp) { 
      if (webApp.ready) webApp.ready(); 
      if (webApp.expand) webApp.expand(); 
    }
    fetchData();
    
    // Safety timeout - ensure loading never gets stuck
    const safetyTimeout = setTimeout(() => {
      setLoading(false);
    }, 15000);
    
    return () => clearTimeout(safetyTimeout);
  }, []);
  
  // Re-fetch when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchData();
    }
  }, [isAuthenticated]);
  
  const handleWebLogin = async (phone, password) => {
    try {
      // Clear any existing session data before login
      localStorage.removeItem('wallet_user');
      localStorage.removeItem('wallet_token');
      setUser(null);
      
      const res = await axios.post(`${API}/wallet/login`, { phone, password });
      if (res.data.success) {
        const userData = {
          ...res.data.user,
          auth_type: 'web'
        };
        setUser(userData);
        setIsAuthenticated(true);
        setAuthMode('authenticated');
        localStorage.setItem('wallet_user', JSON.stringify(userData));
        localStorage.setItem('wallet_token', res.data.token);
        toast.success('Добро пожаловать!');
        return { success: true };
      } else {
        return { success: false, message: res.data.message };
      }
    } catch (e) {
      return { success: false, message: 'Ошибка подключения' };
    }
  };
  
  const handleWebRegister = async (phone, password, firstName, registrationData = null) => {
    // Clear any existing session data before registration
    localStorage.removeItem('wallet_user');
    localStorage.removeItem('wallet_token');
    setUser(null);
    
    // If registrationData is provided, it means registration was completed via verification flow
    if (registrationData && registrationData.success) {
      const userData = {
        ...registrationData.user,
        auth_type: 'web'
      };
      setUser(userData);
      setIsAuthenticated(true);
      setAuthMode('authenticated');
      localStorage.setItem('wallet_user', JSON.stringify(userData));
      localStorage.setItem('wallet_token', registrationData.token);
      return { success: true };
    }
    
    // Fallback to old registration (should not be used with new flow)
    try {
      const res = await axios.post(`${API}/wallet/register`, { phone, password, first_name: firstName });
      if (res.data.success) {
        const userData = {
          ...res.data.user,
          auth_type: 'web'
        };
        setUser(userData);
        setIsAuthenticated(true);
        setAuthMode('authenticated');
        localStorage.setItem('wallet_user', JSON.stringify(userData));
        localStorage.setItem('wallet_token', res.data.token);
        toast.success(t('auth.registrationSuccess'));
        return { success: true };
      } else {
        return { success: false, message: res.data.message };
      }
    } catch (e) {
      return { success: false, message: e.response?.data?.detail || t('errors.somethingWrong') };
    }
  };
  
  const handleLogout = () => {
    const token = localStorage.getItem('wallet_token');
    if (token) axios.post(`${API}/wallet/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    localStorage.removeItem('wallet_user');
    localStorage.removeItem('wallet_token');
    setUser(null);
    setTransactions([]);
    setIsAuthenticated(false);
    setAuthMode('login');
    setPage('home');
  };
  
  const handleNavigate = (newPage) => setPage(newPage);
  const handleBack = () => setPage('home');
  
  // Loading state
  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: SBER.bg }}>
        <div className="flex flex-col items-center gap-2">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: SBER.green }} />
          <span className="text-[14px]" style={{ color: SBER.gray }}>{t('general.loading')}</span>
        </div>
      </div>
    );
  }
  
  // Web Auth Screen (if not in Mini App and not authenticated)
  if (!isInMiniApp && !isAuthenticated) {
    return (
      <WebAuthScreen 
        mode={authMode}
        setMode={setAuthMode}
        onLogin={handleWebLogin}
        onRegister={handleWebRegister}
        t={t}
        language={language}
        setLanguage={setLanguage}
        currentLanguage={currentLanguage}
        languages={languages}
      />
    );
  }
  
  return (
    <div className="wallet-app max-w-[430px] mx-auto min-h-screen relative">
      <AnimatePresence mode="wait">
        {page === 'home' && <WalletHome key="home" user={user} rate={rate} transactions={transactions} onNavigate={handleNavigate} onRefresh={fetchData} notificationCount={notificationCount} onLogout={!isInMiniApp ? handleLogout : null} onUpdateUser={setUser} t={t} />}
        {page === 'notifications' && <NotificationsPage key="notifications" user={user} onBack={handleBack} onNavigate={handleNavigate} t={t} />}
        {page === 'upload-documents' && <DocumentUploadPage key="upload-documents" user={user} onBack={handleBack} onSuccess={() => { fetchNotificationCount(user.telegram_id || user.user_id); handleBack(); }} t={t} />}
        {page === 'topup' && <TopupPage key="topup" rate={rate} onBack={handleBack} onSuccess={fetchData} onNavigate={handleNavigate} t={t} />}
        {page === 'exchange' && <ExchangePage key="exchange" user={user} rate={rate} onBack={handleBack} onSuccess={() => user?.user_id && fetchUserData(user.user_id)} t={t} />}
        {page === 'withdraw' && <WithdrawPage key="withdraw" user={user} rate={rate} onBack={handleBack} t={t} />}
        {page === 'payments' && <PaymentsPage key="payments" user={user} rate={rate} onBack={handleBack} t={t} />}
        {page === 'mia' && <MIAPaymentPage key="mia" user={user} onBack={handleBack} onSuccess={() => user?.user_id && fetchUserData(user.user_id)} t={t} />}
        {page === 'history' && <HistoryPage key="history" transactions={transactions} onBack={handleBack} t={t} />}
        {page === 'profile' && <ProfilePage key="profile" user={user} onBack={handleBack} onNavigate={handleNavigate} onUpdateUser={setUser} t={t} language={language} setLanguage={setLanguage} currentLanguage={currentLanguage} languages={languages} />}
        {page === 'support-chat' && <SupportChatPage key="support-chat" user={user} onBack={handleBack} t={t} />}
      </AnimatePresence>
      
      {['home', 'history', 'profile'].includes(page) && <BottomNav active={page} onNavigate={handleNavigate} t={t} />}
      
      <Toaster 
        position="top-center" 
        toastOptions={{ 
          style: { 
            borderRadius: '12px',
            background: SBER.dark,
            color: SBER.white
          } 
        }} 
      />
    </div>
  );
}
